import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

interface ScrollableCardAreaProps {
    /** Number of 76px cards (plus 12px gap) to show before the area starts scrolling. */
    visibleCards: number;
    children: ReactNode;
}

const CARD_ROW_HEIGHT = 88;

export default function ScrollableCardArea(props: ScrollableCardAreaProps) {
    const { visibleCards, children } = props;
    const theme = useTheme();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const updateScrollState = useCallback(() => {
        const element = scrollRef.current;
        if (!element) return;
        setCanScrollUp(element.scrollTop > 4);
        setCanScrollDown(element.scrollTop + element.clientHeight < element.scrollHeight - 4);
    }, []);

    useEffect(() => {
        updateScrollState();
        const element = scrollRef.current;
        if (!element || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(updateScrollState);
        observer.observe(element);
        Array.from(element.children).forEach(child => observer.observe(child));
        return () => observer.disconnect();
    }, [updateScrollState, children]);

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                ref={scrollRef}
                onScroll={updateScrollState}
                sx={{
                    maxHeight: visibleCards * CARD_ROW_HEIGHT,
                    overflowY: 'auto',
                    scrollbarGutter: 'stable',
                    '&::-webkit-scrollbar': { width: 10 },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'var(--spectre-input)',
                        borderRadius: 5,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'var(--spectre-text-muted)',
                        border: '2px solid transparent',
                        backgroundClip: 'content-box',
                        borderRadius: 5,
                    },
                    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'var(--spectre-navigation-hover)' },
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--spectre-text-muted) var(--spectre-input)',
                }}
            >
                {children}
            </Box>
            {canScrollUp && (
                <Box
                    sx={{
                        background: 'linear-gradient(to bottom, var(--spectre-page), transparent)',
                        height: 20,
                        left: 0,
                        opacity: 0.9,
                        pointerEvents: 'none',
                        position: 'absolute',
                        right: 10,
                        top: 0,
                    }}
                />
            )}
            {canScrollDown && (
                <>
                    <Box
                        sx={{
                            background: 'linear-gradient(to top, var(--spectre-page), transparent)',
                            bottom: 0,
                            height: 32,
                            left: 0,
                            opacity: 0.95,
                            pointerEvents: 'none',
                            position: 'absolute',
                            right: 10,
                        }}
                    />
                    <Box
                        sx={{
                            alignItems: 'center',
                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.spectre.textPrimary : 'var(--spectre-navigation)',
                            border: theme.palette.mode === 'dark' ? '1px solid var(--spectre-border)' : 'none',
                            borderRadius: '50%',
                            bottom: 6,
                            color: theme.palette.mode === 'dark' ? 'var(--spectre-navigation)' : '#FFFFFF',
                            display: 'flex',
                            height: 24,
                            justifyContent: 'center',
                            left: '50%',
                            pointerEvents: 'none',
                            position: 'absolute',
                            transform: 'translateX(-50%)',
                            width: 24,
                        }}
                    >
                        <KeyboardArrowDown fontSize="small" />
                    </Box>
                </>
            )}
        </Box>
    );
}
